import { forwardRef } from 'preact/compat'
import { BRAND_CLASSES } from '@/constants/theme'
import { getLabelClasses, type LabelSize } from './formLabelUtils'

export interface TimePickerProps extends Omit<JSX.IntrinsicElements['input'], 'type'> {
    label?: string
    required?: boolean
    error?: string
    helperText?: string
    containerClassName?: string
    className?: string
    /** Label size variant */
    labelSize?: LabelSize
    /** The input's id, which the label points at; the field name stands in without one. */
    id?: string
    name?: string
    /**
     * Lets the picker run controlled (value/onInput), not only from a register() spread, as DateTimePicker does:
     * preact/compat's forwardRef typing otherwise strips the intrinsic input attributes.
     */
    [key: string]: unknown
}

/**
 * Common TimePicker for a time of day on its own (`HH:mm`), the DatePicker's companion where the date and the
 * time are separate fields. Integrates with React Hook Form via forwardRef, or runs controlled (value/onInput).
 *
 * @example
 * ```tsx
 * <TimePicker label="Start time" required step={300} {...register('start', { required: 'Required' })} />
 * ```
 */
export const TimePicker = forwardRef<HTMLInputElement, TimePickerProps>(
    ({ label, required, error, helperText, containerClassName, className, labelSize, ...props }, ref) => {
        const inputId = props.id ?? props.name
        const inputClasses = `
            w-full px-3 py-2 border rounded-lg
            ${BRAND_CLASSES.INPUT.NORMAL}
            [color-scheme:light] dark:[color-scheme:dark]
            focus:outline-none focus:ring-2 focus:border-transparent
            ${error
                ? BRAND_CLASSES.INPUT.ERROR
                : BRAND_CLASSES.FOCUS.PRIMARY
            }
            ${className || ''}
        `.trim().replace(/\s+/g, ' ')

        return (
            <div className={containerClassName}>
                {label && (
                    <label htmlFor={inputId} className={getLabelClasses(labelSize)}>
                        {label}
                        {required && <span className={BRAND_CLASSES.INPUT.REQUIRED}> *</span>}
                    </label>
                )}
                <input
                    ref={ref}
                    type="time"
                    className={inputClasses}
                    {...props}
                    id={inputId}
                />
                {error && (
                    <p className={`mt-1 text-sm ${BRAND_CLASSES.INPUT.ERROR_TEXT}`}>
                        {error}
                    </p>
                )}
                {!error && helperText && (
                    <p className={`mt-1 text-sm ${BRAND_CLASSES.INPUT.HELPER}`}>
                        {helperText}
                    </p>
                )}
            </div>
        )
    }
)

TimePicker.displayName = 'TimePicker'
